'use client';

import { useState, type Ref } from 'react';
import { theme } from '../theme';
import { SpamGuardInputs, useSpamGuard } from './useSpamGuard';
import { CALC_SMS_CONSENT_TEXT as APPROVED_CALC_CONSENT_TEXT, approvedSmsConsentText, smsConsentFields } from './smsConsent';

/**
 * Pieces the two seller-calculator blocks (NetProceeds, SellVsRent) share: number formatting,
 * the browser id + `?valuation=` token readers, a labelled number input, and the PDF gate —
 * name + email (+ phone, an UNCHECKED SMS-consent checkbox that is never pre-ticked) posted to
 * the tenant site's same-origin /api/calc/unlock. Framework-agnostic (plain elements, no next/*).
 *
 * Analytics: NONE from these blocks — the gateway records proceeds_calculated / sell_vs_rent_run
 * itself when it handles the calculation POSTs.
 */

/**
 * Default TCPA consent checkbox copy — version `v1-2026-09` (docs/SMS_CONSENT.md). The wording
 * and its version are the platform-owned contract in ./smsConsent.ts: an agent-configured
 * `consentText` is only rendered when it is one of the approved wordings, and the words shown are
 * posted with the capture so the gateway derives the stored version id from them.
 */
export const CALC_SMS_CONSENT_TEXT = APPROVED_CALC_CONSENT_TEXT;

export const money = (n: number | null | undefined): string => (typeof n === 'number' && Number.isFinite(n) ? `${n < 0 ? '-' : ''}$${Math.round(Math.abs(n)).toLocaleString('en-US')}` : '—');

export function readBrowserId(): string | undefined {
  try {
    const m = typeof document !== 'undefined' ? document.cookie.match(/(?:^|;\s*)blt_bid=([^;]+)/) : null;
    return m ? decodeURIComponent(m[1]) : undefined;
  } catch {
    return undefined;
  }
}

/** `?valuation=<64-hex token>` — the visitor's AVM report, so the sale price can be prefilled server-side. */
export function readValuationToken(): string | undefined {
  try {
    if (typeof window === 'undefined') return undefined;
    const t = (new URLSearchParams(window.location.search).get('valuation') ?? '').trim();
    return /^[a-f0-9]{64}$/i.test(t) ? t : undefined;
  } catch {
    return undefined;
  }
}

export const pageUrl = (): string | undefined => (typeof window !== 'undefined' ? window.location.href : undefined);

export const FIELD = 'w-full rounded-md border border-black/15 bg-transparent px-3 py-2';
export const PRIMARY_BTN = 'rounded-md px-6 py-2 font-medium hover:opacity-90 disabled:opacity-60';

export function numberOrUndefined(raw: FormDataEntryValue | null): number | undefined {
  const s = String(raw ?? '').trim().replace(/[,$%\s]/g, '');
  if (!s) return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * A labelled number input. `min` defaults to 0 (money never goes negative); a field that may go
 * below zero (appreciation) passes its own floor. `inputRef` lets a block set the value of ONE
 * field programmatically (the rent estimate) without remounting the whole form.
 */
export function NumberField(props: { name: string; label: string; defaultValue?: number | string; placeholder?: string; step?: number; min?: number; prefix?: string; suffix?: string; required?: boolean; help?: string; inputRef?: Ref<HTMLInputElement> }) {
  const id = `calc-${props.name}`;
  return (
    <label htmlFor={id} className="block text-sm">
      <span className="mb-1 block font-medium">{props.label}</span>
      <span className="relative block">
        {props.prefix ? <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 opacity-60">{props.prefix}</span> : null}
        <input
          ref={props.inputRef}
          id={id}
          name={props.name}
          type="number"
          inputMode="decimal"
          defaultValue={props.defaultValue ?? ''}
          placeholder={props.placeholder}
          step={props.step ?? 1}
          min={props.min ?? 0}
          required={props.required}
          className={`${FIELD}${props.prefix ? ' pl-7' : ''}${props.suffix ? ' pr-8' : ''}`}
        />
        {props.suffix ? <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 opacity-60">{props.suffix}</span> : null}
      </span>
      {props.help ? <span className="mt-1 block text-xs opacity-70">{props.help}</span> : null}
    </label>
  );
}

export interface UnlockResponse {
  data?: { lead_id: string; pdf_url: string } | null;
  error?: string | null;
}

export interface PdfGateProps {
  kind: 'net_proceeds' | 'sell_vs_rent';
  runId: string;
  showPhone?: boolean;
  phoneRequired?: boolean;
  consentText?: string;
  /** The open-the-gate button label (default "Get the itemized breakdown (PDF)"). */
  buttonLabel?: string;
  /** Cloudflare Turnstile site key from the host (BlockData.turnstileSiteKey). */
  turnstileSiteKey?: string | null;
}

type GateStep = 'closed' | 'open' | 'sent';

/**
 * The gate in front of the itemized PDF. Opens on a button, collects name + email (+ phone and
 * an unchecked SMS-consent box), POSTs to /api/calc/unlock and shows the "sent to" confirmation
 * with a direct link to the PDF.
 */
export function PdfGate({ kind, runId, showPhone = true, phoneRequired = false, consentText, buttonLabel = 'Get the itemized breakdown (PDF)', turnstileSiteKey }: PdfGateProps) {
  // Platform-owned TCPA copy: an unapproved (agent-edited) wording is never shown, because it
  // could not be recorded against a version id (./smsConsent.ts).
  const shownConsentText = approvedSmsConsentText(consentText, CALC_SMS_CONSENT_TEXT);
  const [step, setStep] = useState<GateStep>('closed');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);
  const [result, setResult] = useState<{ email: string; pdfUrl: string | null } | null>(null);
  // Spam-check inputs for the gate (./useSpamGuard.tsx).
  const guard = useSpamGuard(turnstileSiteKey);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (pending) return;
    const fd = new FormData(e.currentTarget);
    const name = String(fd.get('name') ?? '').trim();
    const email = String(fd.get('email') ?? '').trim();
    const phone = String(fd.get('phone') ?? '').trim();
    const phoneDigits = phone.replace(/\D/g, '').length;
    if (phoneDigits < 7 && (phoneRequired || phoneDigits > 0)) {
      setError(phoneRequired ? 'Please enter a phone number so we can send your breakdown.' : 'Please enter a valid phone number, or leave it blank.');
      return;
    }
    const spam = guard.fields(e.currentTarget);
    setError(null);
    setPending(true);
    try {
      const res = await fetch('/api/calc/unlock', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind, run_id: runId, name, email, phone: phone || undefined,
          // Consent is for a number: without one there is nothing to consent to. The exact words
          // shown travel with it; the gateway derives the stored version id from them.
          ...smsConsentFields({ checked: consent, hasPhone: phoneDigits > 0, shownText: shownConsentText }),
          browser_id: readBrowserId(), page_url: pageUrl(),
          ...spam,
        }),
      });
      const body = (await res.json().catch(() => ({}))) as UnlockResponse;
      if (!res.ok) throw new Error(body.error || `Request failed: ${res.status}`);
      setResult({ email, pdfUrl: body.data?.pdf_url ?? null });
      setStep('sent');
    } catch (err) {
      setError(err instanceof Error && err.message && !/Request failed/.test(err.message) ? err.message : 'Sorry, something went wrong sending your breakdown. Please try again.');
    } finally {
      guard.reset();
      setPending(false);
    }
  }

  if (step === 'sent' && result) {
    return (
      <div className="mt-5 rounded-md bg-emerald-50 px-4 py-4 text-sm text-emerald-900" data-gate="sent">
        <p className="font-medium">Your itemized breakdown is on its way to <strong>{result.email}</strong>.</p>
        {result.pdfUrl ? <p className="mt-2"><a href={result.pdfUrl} target="_blank" rel="noopener" className="font-medium underline">Open the PDF now</a></p> : null}
      </div>
    );
  }

  if (step === 'closed') {
    return (
      <button type="button" onClick={() => setStep('open')} className={`blk-btn ${PRIMARY_BTN} mt-5`} data-gate="closed">
        {buttonLabel}
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-5 space-y-3 rounded-lg border border-black/10 p-4" data-gate="open">
      <p className="text-sm opacity-80">Tell us where to send the itemized PDF.</p>
      {error ? <p className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{error}</p> : null}
      <input type="text" name="name" placeholder="Your name" required autoComplete="name" className={FIELD} />
      <input type="email" name="email" placeholder="you@example.com" required autoComplete="email" className={FIELD} />
      {showPhone || phoneRequired ? (
        <>
          <input type="tel" name="phone" placeholder={phoneRequired ? 'Phone' : 'Phone (optional)'} required={phoneRequired} autoComplete="tel" className={FIELD} />
          <label className="flex items-start gap-2 text-sm opacity-90">
            <input type="checkbox" name="consent_sms" checked={consent} onChange={(e) => setConsent(e.currentTarget.checked)} className="mt-1" />
            <span>{shownConsentText}</span>
          </label>
        </>
      ) : null}
      <SpamGuardInputs guard={guard} />
      <div>
        <button type="submit" disabled={pending} className={`blk-btn ${PRIMARY_BTN}`}>
          {pending ? 'Sending…' : 'Send my breakdown'}
        </button>
        <button type="button" onClick={() => { setError(null); setStep('closed'); }} className="ml-3 text-sm underline opacity-70">Cancel</button>
      </div>
    </form>
  );
}
